import { cn } from '@/lib/utils';

import { isLinkActive } from './active';
import { DISABLED_ITEM_CLASS, NAV_ICON_CLASS, POPOVER_CLASS } from './constants';
import { FloatingPopover } from './FloatingPopover';
import { ICON_MAP } from './iconMap';
import type { LinkItem } from './types';
import { useFloatingMenu } from './useFloatingMenu';

interface SidebarNavItemProps {
  item: LinkItem;
  collapsed: boolean;
  pathname: string;
}

// A single top-level nav link (or disabled placeholder). When the sidebar is
// collapsed, hovering a link reveals a label popover (render_simple_popover).
export function SidebarNavItem({ item, collapsed, pathname }: SidebarNavItemProps) {
  const menu = useFloatingMenu({ enabled: collapsed });
  const Icon = ICON_MAP[item.icon];
  const active = isLinkActive(item, pathname);

  if (item.disabled) {
    return (
      <li>
        <div className={cn('nav-link', DISABLED_ITEM_CLASS)} data-sidebar-navigation-target="navItem">
          <Icon className={NAV_ICON_CLASS} aria-hidden="true" />
          <span className={cn('nav-text', collapsed && 'hidden')}>{item.label}</span>
        </div>
      </li>
    );
  }

  return (
    <li>
      <a
        ref={menu.setReference}
        href={item.href}
        className="nav-link"
        data-nav-active={active}
        data-sidebar-navigation-target="navItem"
        data-turbo-frame="_top"
        onMouseEnter={menu.show}
        onMouseLeave={menu.scheduleHide}
      >
        <Icon className={NAV_ICON_CLASS} aria-hidden="true" />
        <span className={cn('nav-text', collapsed && 'hidden')}>{item.label}</span>
      </a>

      <FloatingPopover menu={menu} className={POPOVER_CLASS}>
        <a href={item.href} className="block px-[18px] py-3 bg-off-white rounded-lg" data-turbo-frame="_top">
          <div className="text-sm font-semibold text-text-secondary">{item.label}</div>
        </a>
      </FloatingPopover>
    </li>
  );
}
